import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import content from "@/content/siteContent";

const CardMedia = ({ item }) => (
  <div className="relative aspect-[4/3] overflow-hidden bg-plz-navy">
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-plz-surface via-plz-navy to-plz-deep">
      <span className="font-heading text-6xl font-extrabold text-plz-line">{item.name.charAt(0)}</span>
    </div>
    <img src={item.image} alt={item.imageAlt || item.name} loading="lazy"
      onError={(e) => { e.currentTarget.style.opacity = "0"; }}
      className="relative h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
    {item.logoOverlay && (
      <img src={item.logoOverlay} alt={`${item.name} logo`} data-testid="mindmoves-logo-overlay"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        className="absolute bottom-3 right-3 w-24 rounded-xl bg-white/95 p-2 shadow-lg" />
    )}
  </div>
);

export default function PortfolioSection() {
  const pf = content.portfolio;
  return (
    <section id={pf.id} data-testid="portfolio-section" className="relative border-t border-plz-line/40 bg-plz-navy/30">
      <div className="mx-auto max-w-7xl scroll-mt-24 px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-plz-orange">{pf.label}</p>
          <h2 className="mt-4 max-w-3xl font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">
            {pf.headlineA}
            <span className="text-plz-orange">{pf.headlineAccent}</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-plz-slate sm:text-lg">{pf.intro}</p>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {pf.items.map((item, i) => (
            <Reveal key={item.name} delay={0.1 * i} className="h-full">
              <article
                data-testid={`portfolio-card-${i}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-plz-line/60 bg-plz-surface/40 transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-plz-orange/50"
              >
                {item.url ? (
                  <a href={item.url} target="_blank" rel="noopener noreferrer" data-testid={`portfolio-image-link-${i}`}
                    className="block">
                    <CardMedia item={item} />
                  </a>
                ) : (
                  <CardMedia item={item} />
                )}
                <div className="flex flex-1 flex-col p-7">
                  <div className="flex items-center justify-between gap-3">
                    {item.url ? (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" data-testid={`portfolio-name-link-${i}`}
                        className="inline-flex items-center gap-1.5 font-heading text-lg font-semibold text-plz-cream transition-colors hover:text-plz-amber">
                        {item.name}
                        <ArrowUpRight className="h-4 w-4 text-plz-orange" aria-hidden="true" />
                      </a>
                    ) : (
                      <h3 className="font-heading text-lg font-semibold">{item.name}</h3>
                    )}
                    <span className="whitespace-nowrap rounded-full border border-plz-orange/40 bg-plz-orange/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-plz-amber">
                      {item.tag}
                    </span>
                  </div>
                  <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-plz-orange">{item.tagline}</p>
                  <p className="mt-3 text-sm leading-relaxed text-plz-slate">{item.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
